import { Fragment, useState } from "react";

import { useSetup } from "../../SetupLife.context";
import { ICapitalInsured } from "../../SetupLife.types";

import { ButtonDelete, Container, FieldWithHint, MultiButton, MultiWrapper, RadioCard, Row, SelectForm, Title, ValidationError } from "./InsuredCapital.styled";
import { Button, Checkbox, ListItemText, MenuItem } from "@mui/material";
import { formatCurrency, isMinAboveMax, maskDecimal } from "./InsuredCapital.utils";
import { TextField } from "../Rules/Rules.styled";
import { useQuery } from "@tanstack/react-query";
import api from "services/api";

export function CapitalInsured() {
  /** Values needed to change the global state */
  const { setup, updateSetup } = useSetup();

  const { data, isFetching, isFetched } = useQuery({
    queryKey: ["organization-roles", setup.rules.rulesRegister.buyerFederalTaxId],
    queryFn: () => api.get(`/v2//operation/organization-roles/${setup.rules.rulesRegister.buyerFederalTaxId}`),
    select: (data) => data?.data?.data as string[],
  });

  function findNegotiations(): string[] {
    const { type } = setup.benefitsPolicy;

    if (type === "UNIQUE") return [setup.benefitsPolicy.benefitsPolicyByUnique.plan].filter((b): b is string => b !== undefined);

    if (type === "ROLE") return setup.benefitsPolicy.benefitsPolicyByRole.map(b => b.planName).filter((b): b is string => b !== undefined);

    if (type === "GEOGRAPHIC_REGION") return setup.benefitsPolicy.benefitsPolicyByGeographicRegion.map(b => b.planName).filter((b): b is string => b !== undefined);

    return []
  }

  function findRoles(negotiationId: string, defaultRoles: string[]) {
    const { type, benefitsPolicyByRole } = setup.benefitsPolicy;

    /** Se a politica for de role, trazer apenas as roles parametrizadas para a negociacao */
    if (type === "ROLE") {
      const rules = benefitsPolicyByRole.find(b => b.planName === negotiationId);

      return rules?.roleName ?? defaultRoles;
    }

    /** Se não for, trazer todas as roles */
    return defaultRoles;
  }

  /** Estado local que possui todos os campos de regra de contrato. */
  const [capitalInsureds, setCapitalInsureds] = useState<ICapitalInsured[]>(setup.capitalInsured);
  const [currentNegotiation, setCurrentNegotiation] = useState<string | null>(findNegotiations()[0] ?? null);

  if (!isFetched && isFetching) return <div>Loading...</div>;

  // /** Opções possíveis da coluna de Cargo. */
  // const roleOptions = setup?.benefitsPolicy?.benefitsPolicyByRole?.map(r => r.roleName)?.filter(r => !!r)?.flat() ?? [];
  /** Opções possíveis da coluna de Cargo. */
  const roleOptions = data ?? [];

  /** Função responsável por validar e salvar as edições feitas nos campos de políticas de benefício. */
  async function handleSave() {
    try {
      //validar...
      updateSetup({ ...setup, capitalInsured: capitalInsureds });
    } catch (error) {
      console.error(error);
    }
  }

  // /** Função responsável por editar os campos de políticas de benefício. */
  async function handleChange(payload: Partial<ICapitalInsured>, negotiationId: string) {
    const updatedPayload = capitalInsureds.map((c) => c.negotiationId === negotiationId ? { ...c, ...payload } : c);

    setCapitalInsureds(updatedPayload);
  }

  function handleUpdateChange(payload: Partial<{ cargos?: string[], valor?: string }>, negotiationId: string, index: number) {
    const capitalInsured = capitalInsureds.find(c => c.negotiationId === negotiationId);
    if (!capitalInsured) return;

    const updatedPayload = capitalInsured.escalonado.grupos?.map((c, i) => i === index ? { ...c, ...payload } : c);

    return handleChange({ ...capitalInsured, escalonado: { ...capitalInsured.escalonado, grupos: updatedPayload }}, negotiationId);
  }

  /** Função responsável por remover campo da array de changes. */
  function handleRemoveChange(negotiationId: string, index: number) {
    const capitalInsured = capitalInsureds.find(c => c.negotiationId === negotiationId);
    if (!capitalInsured) return;

    const updatedPayload = capitalInsured.escalonado.grupos?.filter((_c, i) => i !== index);

    return handleChange({ ...capitalInsured, escalonado: { ...capitalInsured.escalonado, grupos: updatedPayload }}, negotiationId);
  }

  function handleAddChange(negotiationId: string) {
    const capitalInsured = capitalInsureds.find(c => c.negotiationId === negotiationId);
    if (!capitalInsured) return;

    const newChange = { cargos: undefined, valor: undefined };

    // Garante que grupos seja um array, mesmo que seja undefined/null
    const currentGroups = capitalInsured.escalonado.grupos ?? [];

    return handleChange({ ...capitalInsured, escalonado: { ...capitalInsured.escalonado, grupos: [...currentGroups, newChange]}}, negotiationId);
  }

  /** Função para obter cargos disponíveis (não desabilitados) para um índice específico de um capitalInsured. */
  function getAvailableRolesForCapital(negotiationId: string, index: number) {
    const capitalInsured = capitalInsureds.find(c => c.negotiationId === negotiationId);
    if (!capitalInsured) return [];

    const rolesForNegotiation = findRoles(negotiationId, roleOptions);

    return rolesForNegotiation.filter(option =>
      !capitalInsured.escalonado.grupos?.some((g, i) => i !== index && g.cargos?.includes(option))
    );
  }

  /** Função para manipular seleção de "Selecionar Todos" no SelectForm de cargos. */
  function handleSelectAllRolesForCapital(negotiationId: string, index: number) {
    const capitalInsured = capitalInsureds.find(c => c.negotiationId === negotiationId);
    if (!capitalInsured) return;

    const currentValue = capitalInsured.escalonado.grupos?.[index]?.cargos ?? [];
    const availableRoles = getAvailableRolesForCapital(negotiationId, index);
    const allSelected = availableRoles.every(role => currentValue.includes(role));

    if (allSelected) {
      // Desselecionar todos
      handleUpdateChange({ cargos: [] }, negotiationId, index);
    } else {
      // Selecionar todos os disponíveis
      handleUpdateChange({ cargos: availableRoles }, negotiationId, index);
    }
  }

  return (
    <>
      <Container>
        {findNegotiations().length > 0 && (
          <MultiWrapper>
            {findNegotiations().map(negotiation => (
              <MultiButton variant={negotiation === currentNegotiation ? "active" : "inactive"} onClick={() => setCurrentNegotiation(negotiation)}>{negotiation}</MultiButton>
            ))}
          </MultiWrapper>
        )}
        <Title>Capital Segurado</Title>
        {capitalInsureds.map(capitalInsured => (
          <Fragment key={capitalInsured.negotiationId}>
            {capitalInsured.negotiationId === currentNegotiation && (
              <>
                <Row>
                  <RadioCard
                    label="Valor Uniforme"
                    selected={capitalInsured.tipoCapital === "VALOR_UNIFORME"}
                    onClick={() => handleChange({ tipoCapital: "VALOR_UNIFORME" }, capitalInsured.negotiationId)}
                  />
                  <RadioCard
                    label="Múltiplo Salarial"
                    selected={capitalInsured.tipoCapital === "MULTIPLO_SALARIAL"}
                    onClick={() => handleChange({ tipoCapital: "MULTIPLO_SALARIAL" }, capitalInsured.negotiationId)}
                  />
                  <RadioCard
                    label="Escalonado por Cargo/Grupo"
                    selected={capitalInsured.tipoCapital === "ESCALONADO"}
                    onClick={() => handleChange({ tipoCapital: "ESCALONADO" }, capitalInsured.negotiationId)}
                  />
                  <RadioCard
                    label="Faixa etária"
                    selected={capitalInsured.tipoCapital === "FAIXA_ETARIA"}
                    onClick={() => handleChange({ tipoCapital: "FAIXA_ETARIA" }, capitalInsured.negotiationId)}
                  />
                </Row>
                <Row>
                  {capitalInsured.tipoCapital === "VALOR_UNIFORME" && (
                    <>
                      <TextField
                        label="Valor da Cobertura (R$)"
                        value={formatCurrency(capitalInsured.valorUniforme.valor)}
                        onChange={(e) => handleChange({ ...capitalInsured, valorUniforme: { ...capitalInsured.valorUniforme, valor: e.target.value as string }}, capitalInsured.negotiationId)}
                      />
                      <TextField
                        label="Taxa de cálculo de prêmio"
                        value={maskDecimal(capitalInsured.valorUniforme.taxa)}
                        onChange={(e) => handleChange({ ...capitalInsured, valorUniforme: { ...capitalInsured.valorUniforme, taxa: e.target.value as string }}, capitalInsured.negotiationId)}
                      />
                    </>
                  )}
                  {capitalInsured.tipoCapital === "MULTIPLO_SALARIAL" && (
                    <>
                      <TextField
                        label="Múltiplo"
                        value={capitalInsured.multiploSalarial.multiplo}
                        onChange={(e) => handleChange({ ...capitalInsured, multiploSalarial: { ...capitalInsured.multiploSalarial, multiplo: e.target.value as string }}, capitalInsured.negotiationId)}
                      />
                      <TextField
                        label="Valor Mínimo (R$)"
                        value={formatCurrency(capitalInsured.multiploSalarial.valorMinimo)}
                        onChange={(e) => handleChange({ ...capitalInsured, multiploSalarial: { ...capitalInsured.multiploSalarial, valorMinimo: e.target.value as string }}, capitalInsured.negotiationId)}
                      />
                      <FieldWithHint>
                        <TextField
                          label="Valor Máximo (R$)"
                          value={formatCurrency(capitalInsured.multiploSalarial.valorMaximo)}
                          onChange={(e) => handleChange({ ...capitalInsured, multiploSalarial: { ...capitalInsured.multiploSalarial, valorMaximo: e.target.value as string }}, capitalInsured.negotiationId)}
                        />
                        {/* Aviso não-bloqueante: o backend limita (clamp) a cobertura à faixa,
                            então uma faixa invertida degrada de forma previsível em vez de gerar erro. */}
                        {isMinAboveMax(capitalInsured.multiploSalarial.valorMinimo, capitalInsured.multiploSalarial.valorMaximo) && (
                          <ValidationError>Valor Mínimo não pode ser maior que Valor Máximo</ValidationError>
                        )}
                      </FieldWithHint>
                      <TextField
                        label="Taxa de cálculo de prêmio"
                        value={maskDecimal(capitalInsured.multiploSalarial.taxa)}
                        onChange={(e) => handleChange({ ...capitalInsured, multiploSalarial: { ...capitalInsured.multiploSalarial, taxa: e.target.value as string }}, capitalInsured.negotiationId)}
                      />
                    </>
                  )}
                  {capitalInsured.tipoCapital === "ESCALONADO" && (
                    <>
                      <TextField
                        label="Taxa de cálculo de prêmio"
                        value={maskDecimal(capitalInsured.escalonado.taxa)}
                        onChange={(e) => handleChange({ ...capitalInsured, escalonado: { ...capitalInsured.escalonado, taxa: e.target.value as string }}, capitalInsured.negotiationId)}
                      />
                    </>
                  )}
                  {capitalInsured.tipoCapital === "FAIXA_ETARIA" && (
                    <>
                      <TextField
                        label="Valor da Cobertura (R$)"
                        value={formatCurrency(capitalInsured.faixaEtaria.valor)}
                        onChange={(e) => handleChange({ ...capitalInsured, faixaEtaria: { ...capitalInsured.faixaEtaria, valor: e.target.value as string }}, capitalInsured.negotiationId)}
                      />
                      <TextField
                        label="Valor superior a idade corte (R$)"
                        value={formatCurrency(capitalInsured.faixaEtaria.valorSuperior)}
                        onChange={(e) => handleChange({ ...capitalInsured, faixaEtaria: { ...capitalInsured.faixaEtaria, valorSuperior: e.target.value as string }}, capitalInsured.negotiationId)}
                      />
                      <TextField
                        label="Taxa de cálculo de prêmio"
                        value={maskDecimal(capitalInsured.faixaEtaria.taxa)}
                        onChange={(e) => handleChange({ ...capitalInsured, faixaEtaria: { ...capitalInsured.faixaEtaria, taxa: e.target.value as string }}, capitalInsured.negotiationId)}
                      />
                      <TextField
                        label="Taxa para idades superiores ao corte"
                        value={maskDecimal(capitalInsured.faixaEtaria.taxaSuperior)}
                        onChange={(e) => handleChange({ ...capitalInsured, faixaEtaria: { ...capitalInsured.faixaEtaria, taxaSuperior: e.target.value as string }}, capitalInsured.negotiationId)}
                      />
                    </>
                  )}
                </Row>
                {capitalInsured.tipoCapital === "FAIXA_ETARIA" && (
                  <Row>
                    <TextField
                      label="Idade corte"
                      value={capitalInsured.faixaEtaria.idade}
                      onChange={(e) => handleChange({ ...capitalInsured, faixaEtaria: { ...capitalInsured.faixaEtaria, idade: e.target.value as string }}, capitalInsured.negotiationId)}
                    />
                  </Row>
                )}
                {capitalInsured.tipoCapital === "ESCALONADO" && (
                  <>
                    {capitalInsured.escalonado.grupos?.map((row, index) => (
                      <Row key={`${capitalInsured.negotiationId}_${index}`} style={{ alignItems: "center"}}>
                        <SelectForm
                          label="Cargos"
                          multiple
                          size="small"
                          value={row.cargos ?? []}
                          onChange={(e) => {
                            const value = e.target.value as string[];

                            // Verifica se "SELECT_ALL" foi clicado
                            if (value.includes("SELECT_ALL")) {
                              handleSelectAllRolesForCapital(capitalInsured.negotiationId, index);
                            } else {
                              handleUpdateChange({ cargos: [...value] }, capitalInsured.negotiationId, index);
                            }
                          }}
                          renderValue={(selected) => (selected as string[]).join(", ")}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                              },
                            },
                          }}
                        >
                          <MenuItem value="SELECT_ALL" dense>
                            <Checkbox
                              size="small"
                              checked={
                                getAvailableRolesForCapital(capitalInsured.negotiationId, index).length > 0 &&
                                getAvailableRolesForCapital(capitalInsured.negotiationId, index).every(role => (row.cargos ?? []).includes(role))
                              }
                              indeterminate={
                                (row.cargos ?? []).length > 0 &&
                                !getAvailableRolesForCapital(capitalInsured.negotiationId, index).every(role => (row.cargos ?? []).includes(role))
                              }
                              sx={{ py: 0 }}
                            />
                            <ListItemText
                              primary="Selecionar Todos"
                              primaryTypographyProps={{ fontSize: '0.875rem' }}
                            />
                          </MenuItem>
                          {findRoles(capitalInsured.negotiationId, roleOptions).sort().map(option => (
                            <MenuItem
                              key={option}
                              value={option}
                              dense
                              disabled={capitalInsured.escalonado.grupos.some((g, i) => i !== index && g.cargos?.includes(option))}
                            >
                              <Checkbox
                                size="small"
                                checked={(row.cargos ?? []).includes(option)}
                                sx={{ py: 0 }}
                              />
                              <ListItemText
                                primary={option}
                                primaryTypographyProps={{ fontSize: '0.875rem' }}
                              />
                            </MenuItem>
                          ))}
                        </SelectForm>
                        <TextField
                          label="Valor da Cobertura (R$)"
                          value={formatCurrency(row.valor)}
                          onChange={(e) => handleUpdateChange({ valor: e.target.value }, capitalInsured.negotiationId, index)}
                        />
                        <ButtonDelete color="error" onClick={() => handleRemoveChange(capitalInsured.negotiationId, index)} />
                      </Row>
                    ))}
                    <Button onClick={() => handleAddChange(capitalInsured.negotiationId)}>+ Adicionar alteração</Button>
                  </>
                )}
                <Title>Regras de Produto</Title>
                <Row>
                  <SelectForm
                    label="Requer DPS (Declaração de Saúde)?"
                    value={capitalInsured.regrasProduto?.dps}
                    onChange={(e) => handleChange({ ...capitalInsured, regrasProduto: { dps: e.target.value as string }}, capitalInsured.negotiationId)}
                  >
                    <MenuItem value={"SIM"}>Sim</MenuItem>
                    <MenuItem value={"NAO"}>Nao</MenuItem>
                  </SelectForm>
                </Row>
              </>
            )}
          </Fragment>
        ))}
      </Container>
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          gap: "8px",
          marginTop: "50px",
        }}
      >
        <Button
            variant="contained"
            style={{
              paddingTop: "0.25rem",
              paddingBottom: "0.25rem",
              paddingLeft: "1rem",
              paddingRight: "1rem",
              borderRadius: "0.375rem",
              height: "2.5rem",
              fontSize: "0.875rem",
              lineHeight: "1.25rem",
              fontWeight: 600,
              width: "130px",
              marginLeft: "50px"
            }}
            onClick={handleSave}
          >
            {"Salvar"}
          </Button>
      </div>
    </>
  );
}
